import { type ReactNode, useMemo } from "react";
import { Link } from "wouter";
import { ArrowRight, ChevronRight, Clock } from "lucide-react";
import { usePublicPageMeta } from "@/hooks/usePublicPageMeta";
import { getPlatformConfig } from "@/lib/platform/config";
import { getCanonicalPublicOrigin } from "@/lib/canonical-origin";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { renderBlocks, type Block } from "@/components/legal/LegalContent";
import type { FaqItem, GuideArticle } from "@/content/guides";

interface BreadcrumbEntry {
  label: string;
  href: string;
}

function buildJsonLd(input: {
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author: string;
  section?: string;
  faqs: FaqItem[];
  breadcrumbs: BreadcrumbEntry[];
}) {
  const config = getPlatformConfig();
  const siteUrl = getCanonicalPublicOrigin();
  const graph: Record<string, unknown>[] = [
    {
      "@type": "BlogPosting",
      "@id": input.url,
      headline: input.headline,
      description: input.description,
      url: input.url,
      mainEntityOfPage: input.url,
      ...(input.image ? { image: input.image } : {}),
      ...(input.datePublished ? { datePublished: input.datePublished } : {}),
      ...(input.dateModified ? { dateModified: input.dateModified } : {}),
      ...(input.section ? { articleSection: input.section } : {}),
      author: { "@type": "Organization", name: input.author, url: siteUrl },
      publisher: { "@type": "Organization", name: config.name, url: siteUrl },
      isPartOf: { "@type": "WebSite", name: config.name, url: siteUrl },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: input.breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: b.href.startsWith("http") ? b.href : `${siteUrl}${b.href}`,
      })),
    },
  ];
  if (input.faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: input.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export function GuideArticleLayout({
  article,
  categoryTitle,
  related,
}: {
  article: GuideArticle;
  categoryTitle: string;
  related: ReactNode;
}) {
  const { t } = useAppTranslation();
  const canonicalPath = `/guides/${article.slug}`;
  const categoryPath = `/guides/category/${article.category}`;

  const breadcrumbs: BreadcrumbEntry[] = [
    { label: t("geo.breadcrumb_home"), href: "/" },
    { label: t("guides.breadcrumb_guides"), href: "/guides" },
    { label: categoryTitle, href: categoryPath },
    { label: article.title, href: canonicalPath },
  ];

  usePublicPageMeta(useMemo(() => {
    const url = `${getCanonicalPublicOrigin()}${canonicalPath}`;
    return ({
      title: article.metaTitle,
      description: article.metaDescription,
      canonical: url,
      ogType: "article",
      ogImage: article.hero.image,
      preloadImage: article.hero.image,
      jsonLd: buildJsonLd({
        headline: article.title,
        description: article.metaDescription,
        url,
        image: article.hero.image,
        datePublished: article.publishedAt,
        dateModified: article.updatedAt,
        author: article.author,
        section: categoryTitle,
        faqs: article.faqs,
        breadcrumbs,
      }),
    });
  }, [
    canonicalPath,
    article.metaTitle,
    article.metaDescription,
    article.hero.image,
    article.title,
    article.publishedAt,
    article.updatedAt,
    article.author,
    categoryTitle,
    article.faqs,
    breadcrumbs,
  ]));

  return (
    <article>
      <section className="relative flex min-h-[44vh] items-center justify-center overflow-hidden bg-foreground">
        <img
          src={article.hero.image}
          alt={article.hero.alt}
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center">
          <nav aria-label={t("geo.breadcrumb")} className="mb-4 flex justify-center text-xs text-white/70">
            <ol className="flex flex-wrap items-center justify-center gap-1">
              {breadcrumbs.map((b, i) => (
                <li key={b.href} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3 w-3" />}
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-white/80">{b.label}</span>
                  ) : (
                    <Link href={b.href} className="hover:underline">
                      {b.label}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <h1 className="font-sans text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            {article.title}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
            {article.excerpt}
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 text-xs text-white/70">
            <span>{article.author}</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {t("guides.reading_time", { minutes: article.readingMinutes })}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 text-base text-foreground/90 sm:px-6 lg:px-8">
          {renderBlocks(article.body, `guide-${article.slug}`, {})}

          {article.commercialLinks.length > 0 && (
            <div className="mt-10 rounded-xl border border-primary/30 bg-primary/5 p-5 sm:p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                {t("guides.explore_heading")}
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {article.commercialLinks.map((l) => (
                  <li key={l.href + l.label}>
                    <Link
                      href={l.href}
                      className="cursor-pointer inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
                    >
                      {l.label}
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {article.faqs.length > 0 && (
        <section className="bg-muted/30 py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-sans text-2xl font-bold text-foreground sm:text-3xl">
              {t("geo.faq_heading")}
            </h2>
            <dl className="mt-6 divide-y divide-border">
              {article.faqs.map((f, i) => (
                <div key={i} className="py-5">
                  <dt className="text-base font-semibold text-foreground">{f.q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {related && (
        <section className="bg-background py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{related}</div>
        </section>
      )}

      <section className="bg-foreground py-12 text-background">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white">{t("geo.find_your_classic")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-white/80">{t("geo.cta_description")}</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href="/search"
              className="primary-action cursor-pointer inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold"
            >
              {t("geo.browse_cars")}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href="/list-your-car"
              className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-white/30 bg-transparent px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              {t("geo.list_your_car")}
            </Link>
          </div>
          <p className="mt-6 text-xs text-white/50">{t("geo.last_reviewed", { date: article.updatedAt })}</p>
        </div>
      </section>
    </article>
  );
}

export type { Block };
